import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useSetupNavigation, getNextStepRoute, getNextStepInfo } from "../../hooks/useSetupNavigation";
import { useEmailVerification } from "../../hooks/useEmailVerification";

const COLLAPSE_KEY = "setupBannerCollapsed";

/**
 * Global next-step banner shown on all protected pages except /dashboard.
 * Renders while setup is incomplete; collapses to a "Resume setup" pill on dismiss.
 *
 * The final "go live" step needs the slug + publish action that only live on the
 * booking-page form, so the banner CTA for that step simply navigates there — the
 * booking form handles email verification inline (save config → verify → publish in
 * one action). The banner never does verification in-place itself.
 */
const SetupProgressBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(
    () => sessionStorage.getItem(COLLAPSE_KEY) === "true"
  );

  const { setupProgress, isSetupComplete, isSetupLoading } = useSetupNavigation();
  const { isEmailVerified } = useEmailVerification();

  // Never render on /dashboard (the onboarding checklist lives there)
  if (location.pathname === "/dashboard") return null;

  // Wait until data is loaded before deciding to render
  if (isSetupLoading || !setupProgress) return null;

  // Hide once all 3 steps are done
  if (isSetupComplete) return null;

  const nextRoute = getNextStepRoute(setupProgress);
  const nextInfo = getNextStepInfo(setupProgress);

  // Count completed steps for progress display
  const stepsCompleted = [
    setupProgress.availabilityCompleted,
    setupProgress.serviceCompleted,
    setupProgress.bookingLinkCompleted,
  ].filter(Boolean).length;

  // The final step is the booking page. When the user still needs to verify their
  // email, the message points them at the booking form where they'll publish.
  const isGoLiveStep =
    !setupProgress.bookingLinkCompleted &&
    setupProgress.availabilityCompleted &&
    setupProgress.serviceCompleted;
  const needsVerifyToPublish = isGoLiveStep && !isEmailVerified;

  const handleCollapse = () => {
    sessionStorage.setItem(COLLAPSE_KEY, "true");
    setCollapsed(true);
  };

  const handleExpand = () => {
    sessionStorage.removeItem(COLLAPSE_KEY);
    setCollapsed(false);
  };

  const handleCtaClick = () => {
    if (nextRoute) navigate(nextRoute);
  };

  if (collapsed) {
    return (
      <div className="bg-accent-50 border-b border-accent-100">
        <div className="px-4 py-2 sm:px-6 lg:px-8 flex items-center justify-between">
          <button
            type="button"
            onClick={handleExpand}
            className="flex items-center gap-2 text-sm font-medium text-accent-700 hover:text-accent-600"
          >
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            Resume setup ({stepsCompleted}/3 complete)
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b bg-accent-50 border-accent-100">
      <div className="px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-sm text-accent-700">
            <span className="font-semibold">Setup {stepsCompleted}/3 complete.</span>{" "}
            {needsVerifyToPublish
              ? "Finish setting up your booking page to go live."
              : nextInfo?.message}
          </p>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleCtaClick}
              className="inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-sm font-medium bg-white text-accent-700 ring-1 ring-inset ring-accent-200 hover:bg-accent-50 transition-colors"
            >
              {needsVerifyToPublish ? "Finish setting up" : nextInfo?.buttonText}
              <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={handleCollapse}
              className="p-1 rounded hover:bg-black/10 text-accent-400"
              aria-label="Collapse setup banner"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupProgressBanner;
